import { AccessRight } from "./AccessControlTypes";
import { RawLever } from "./NegotiationTypes";
import { NegotiationStatus, ProjectAction } from "./ProjectTypes";
import type { Vendor } from "./VendorTypes";

export enum OrderFormStatus {
    EXPIRATION_NOT_SET = "not_set",
    RENEWAL_SCHEDULED = "renewal_scheduled",
    WILL_NOT_RENEW = "will_not_renew",
    OVERDUE = "overdue",
    RENEWED = "renewed",
}

export interface OrderForm {
    id: number;
    name: string;
    status: OrderFormStatus;
    negotiationStatus: NegotiationStatus;
    orderStartDate?: Date;
    recordCreationDate: Date;
    orderEndDate?: Date;
    migrationLength?: number;
    value: number;
    autoRenewal: boolean;
    vendorKey: string;
    vendor?: Vendor;
    creatorId: number;
    ownerId: number;
    businessOwnerEmail: string;
    dealRating: DealRating;
    category: string;
    legalWeight: number;
    centsValue: number;
    utilizationScore: number;
    orderScore: number;
    increase?: number;
    decrease?: number;
    optimizedScore: number;
    contractType?: ContractType;
    projectId?: number;
    targetedSavings?: number;
    realizedSavings?: number;
    /**
     * The deal's category path (root first) for tallying spend: the project's
     * category when the org has its own taxonomy, the vendor's IL spend
     * category otherwise. Unset when neither applies.
     */
    spendCategory?: string[];
    /** The owner of the deal's project, when the project is loaded and has one. */
    projectOwner?: { id: number; name: string };
    /** What the user may do on the deal's project, when the list resolved it. */
    projectActions?: ProjectAction[];
    // "enriched"
    topNegotiationStrategies?: string;
}

/** A cancelled deal: its savings were identified, never pursued. */
export const isCancelledDeal = (orderForm: OrderForm): boolean =>
    orderForm.negotiationStatus === NegotiationStatus.CANCELLED;

/** A deal still open (not signed, not cancelled): its savings are on the table, not banked. */
export const isOpenDeal = (orderForm: OrderForm): boolean =>
    orderForm.negotiationStatus !== NegotiationStatus.COMPLETE && !isCancelledDeal(orderForm);

/** Whether the user may take `action` on the deal's project, as the list resolved it. */
export const canDoOnDeal = (orderForm: OrderForm, action: ProjectAction): boolean =>
    orderForm.projectActions?.includes(action) ?? false;

/** The deal's score, or undefined while it has none: a 0 means it was never scored. */
export const orderFormScore = (orderForm: OrderForm): number | undefined =>
    Number.isFinite(orderForm.orderScore) && orderForm.orderScore > 0
        ? orderForm.orderScore
        : undefined;

export interface OrderFormSKU {
    id: number;
    orderFormId: number;
    name: string;
    sku: string;
    quantity: number;
    price: number;
    totalPrice: number;
    usage: number;
}

export interface OrderFormResponse {
    orderForm: OrderForm;
    access: AccessRight;
}

export interface OrderFormUpdateResponse extends OrderForm {}

export enum DealRating {
    A = 1,
    B = 2,
    C = 3,
    D = 4,
    E = 5,
    F = 6,
    NONE = 0,
}
export enum OFOPerationType {
    ADD = "ADD",
    REMOVE = "REMOVE",
}

export interface OrderFormGroupOperation {
    type: OFOPerationType;
    values: number[];
}

export interface OrderFormGroupOperationResponse {
    type: OFOPerationType;
    values: number[];
}

export interface OrderFormLever {
    id: number;
    leverId: number;
    orderFormId: number;
    selectedOptionId: number;
    recordCreationDate: Date;
}

export interface OrderFormLeverDetail extends OrderFormLever {
    isSelected: boolean;
    lever: RawLever;
}

export interface OrderFormAnalysis {
    id: number;
    orderFormId: number;
    analysisId: number;
    selectedOptionId: number;
}

export interface Analysis {
    id: number;
    title: string;
    description: string;
    score: number;
    options: AnalysisOption[];
}

export interface AnalysisOption {
    id: number;
    title: string;
    weight: number;
}

export interface OrderFormAnalysisResponse extends Partial<OrderFormAnalysis> {
    analysis: Analysis;
}

export enum ContractType {
    MSA = "MSA",
    Order = "Order",
    Amendment = "Amendment",
    Other = "Other",
    IncorporatedElsewhere = "IncorporatedElsewhere",
}
